// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// palette.ts — every colour that carries meaning, in one place, so tests/contrast.test.ts can hold
// each one to 3:1 against the surface it is ACTUALLY drawn on (WCAG 2.1 §1.4.11).
//
// A sibling game in this fleet drew its walls at 1.14:1 against the board — right cells, right size,
// completely invisible — and every geometry check passed, because a shape that is on-screen,
// correctly sized and the same colour as its background is geometrically perfect. On a dark palette
// invisible reads as ATMOSPHERE in a screenshot. So the numbers live here rather than as literals in
// the renderer, and MARK_SURFACES declares which pairs are real (an ore numeral never appears on a
// ridge; a wreck never sits on one either, because a ridge is impassable).
//
// COLOUR IS NEVER THE ONLY CHANNEL. Ownership is hue AND fill style AND glyph orientation; unit type
// is silhouette alone; hit points are the glyph's FILL HEIGHT rather than a bar. In pure greyscale
// the board is still playable, which is what makes the cyan/amber pair safe for deuteranopia and
// protanopia (it is a blue/orange axis, never red/green).

export const PALETTE = {
  /** The void behind the board. */
  ground: '#12161c',
  /** An ordinary passable tile — the reference surface almost everything is drawn on. */
  open: '#1c2029',
  /** Impassable, blocks line of fire. Deliberately light: a wall should read as a wall. */
  ridge: '#63708a',
  /** Passable at 60% speed and opaque to line of fire. The HATCH carries the meaning. */
  scrub: '#253a2c',
  scrubhatch: '#6f9c78',
  /** The HUD behind the buttons. */
  panel: '#171b22',
  /** Body copy on the panel. */
  text: '#e6eaf0',
  /** Drawn ON a bright fill — an ore numeral inside a claimed seam, a glyph inside a unit. */
  ink: '#0b0e13',

  /** Player 0 — always drawn as "you" from your own seat. */
  you: '#4fc3f7',
  /** Player 1. */
  foe: '#ffb74d',
  /** An unclaimed seam, and the brass numeral on it. */
  seam: '#e8c86a',
  /** A wreck: money, and cover, and neither for long. */
  wreck: '#9aa3ae',
  /** The selection ring, drawn on the board OUTSIDE the glyph — never on top of it. */
  ring: '#ffffff',
  /** An order line's chevron, and the rust vignette. */
  order: '#c8d2e0',
  rust: '#d98a3a',
} as const;

export type PaletteKey = keyof typeof PALETTE;

/** Surfaces a mark can be drawn on. Everything else in PALETTE is a mark. */
export const SURFACES = ['open', 'scrub', 'panel', 'ground', 'you', 'foe', 'seam'] as const;
export type Surface = (typeof SURFACES)[number];

/** Every mark that carries meaning on the play surface. */
export const ON_BOARD = [
  'you',
  'foe',
  'seam',
  'wreck',
  'ridge',
  'scrubhatch',
  'ring',
  'order',
  'ink',
] as const;

/**
 * Where each mark can ACTUALLY appear. A blanket cross-product would be a lie in both directions:
 * a wreck can never sit on a ridge (impassable), and `ink` only ever appears inside a bright fill.
 */
export const MARK_SURFACES: Record<(typeof ON_BOARD)[number], readonly Surface[]> = {
  you: ['open', 'scrub', 'ground'],
  foe: ['open', 'scrub', 'ground'],
  seam: ['open'],
  wreck: ['open', 'scrub'],
  ridge: ['open', 'ground'],
  scrubhatch: ['scrub', 'open'],
  ring: ['open', 'scrub'],
  order: ['open', 'scrub'],
  // The one mark that lives on the bright fills rather than on the board.
  ink: ['you', 'foe', 'seam'],
};

/** Seat colours, indexed by player. Two seats only — Salient is 1v1. */
export const SEAT_COLOUR: readonly string[] = [PALETTE.you, PALETTE.foe];
export const SEAT_NAME: readonly string[] = ['Blue', 'Amber'];

/**
 * The unit silhouettes, as unit-square polygons pointing "up-field" (−y). The renderer scales these
 * and flips them for the far side, so TYPE is carried by shape alone and survives greyscale at 14px.
 * They are deliberately as different as three convex outlines can be: a narrow triangle, a wide
 * kite, a bevelled square.
 */
/**
 * The size ladder, as a fraction of a tile. Shape alone is not quite enough at 30px: a 1.5x step in
 * area between the three makes the type readable from across the board before the outline resolves.
 * It lives here rather than as three literals in the renderer so the contrast test can assert it is
 * a strict ladder.
 */
export const GLYPH_SCALE: Record<string, number> = {
  harrier: 0.52,
  arbalest: 0.64,
  anvil: 0.78,
};

export const GLYPH: Record<string, ReadonlyArray<readonly [number, number]>> = {
  harrier: [
    [0, -0.5],
    [0.34, 0.42],
    [0, 0.16],
    [-0.34, 0.42],
  ],
  arbalest: [
    [0, -0.5],
    [0.46, 0],
    [0, 0.5],
    [-0.46, 0],
  ],
  anvil: [
    [-0.34, -0.44],
    [0.34, -0.44],
    [0.46, -0.16],
    [0.46, 0.3],
    [0.28, 0.46],
    [-0.28, 0.46],
    [-0.46, 0.3],
    [-0.46, -0.16],
  ],
};
